import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Users, Search, UserPlus, MoreHorizontal, Edit, Mail, UserX, UserCheck } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { NewUserDialog } from '@/components/Users/NewUserDialog';
import { EditUserDialog } from '@/components/Users/EditUserDialog';

interface ClientUsersDialogProps {
  clientId: string | null;
  clientName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ClientUsersDialog = ({
  clientId,
  clientName,
  open,
  onOpenChange,
}: ClientUsersDialogProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isNewUserOpen, setIsNewUserOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const queryClient = useQueryClient();

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['client-users', clientId],
    queryFn: async () => {
      if (!clientId) return [];
      
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .or(`client_id.eq.${clientId},client_ids.cs.{${clientId}}`);

      if (error) throw error;
      return data;
    },
    enabled: !!clientId && open,
  });

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleToggleStatus = async (userId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    
    const { error } = await supabase
      .from('users')
      .update({ status: newStatus })
      .eq('id', userId);

    if (error) {
      toast({
        title: 'Erro',
        description: 'Erro ao atualizar status do usuário',
        variant: 'destructive',
      });
      return;
    }

    toast({
      title: 'Sucesso',
      description: `Usuário ${newStatus === 'active' ? 'ativado' : 'desativado'} com sucesso`,
    });
    
    queryClient.invalidateQueries({ queryKey: ['client-users', clientId] });
  };

  const handleSendInvite = async (user: any) => {
    try {
      const { error } = await supabase.functions.invoke('send-user-invite', {
        body: { email: user.email, name: user.name },
      });

      if (error) throw error;

      toast({
        title: 'Convite enviado',
        description: `Convite enviado para ${user.email}`,
      });
    } catch (error) {
      console.error('Error sending invite:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao enviar convite',
        variant: 'destructive',
      });
    }
  };

  const getRoleBadge = (role: string) => {
    const roleColors: Record<string, 'default' | 'secondary' | 'outline'> = {
      admin: 'default',
      super_admin: 'default',
      client_admin: 'secondary',
      technician: 'outline',
      user: 'outline',
    };

    const roleLabels: Record<string, string> = {
      admin: 'Admin',
      super_admin: 'Super Admin',
      client_admin: 'Admin Cliente',
      technician: 'Técnico',
      user: 'Usuário',
    };

    return (
      <Badge variant={roleColors[role] || 'outline'}>
        {roleLabels[role] || role}
      </Badge>
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Usuários do Cliente
            </DialogTitle>
            <DialogDescription>
              Gerenciar usuários de <strong>{clientName}</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center justify-between gap-4 py-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar usuários..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button onClick={() => setIsNewUserOpen(true)}>
              <UserPlus className="h-4 w-4 mr-2" />
              Novo Usuário
            </Button>
          </div>

          <div className="flex-1 overflow-auto">
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-14 w-full" />
                ))}
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {searchTerm ? 'Nenhum usuário encontrado' : 'Nenhum usuário cadastrado'}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Função</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{getRoleBadge(user.role)}</TableCell>
                      <TableCell>
                        <Badge variant={user.status === 'active' ? 'default' : 'secondary'}>
                          {user.status === 'active' ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setEditingUser(user)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleSendInvite(user)}>
                              <Mail className="h-4 w-4 mr-2" />
                              Enviar Convite
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleToggleStatus(user.id, user.status)}
                            >
                              {user.status === 'active' ? (
                                <>
                                  <UserX className="h-4 w-4 mr-2" />
                                  Desativar
                                </>
                              ) : (
                                <>
                                  <UserCheck className="h-4 w-4 mr-2" />
                                  Ativar
                                </>
                              )}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          <div className="text-sm text-muted-foreground pt-2 border-t">
            Total: {filteredUsers.length} usuário(s)
          </div>
        </DialogContent>
      </Dialog>

      <NewUserDialog
        open={isNewUserOpen}
        onOpenChange={(open) => {
          setIsNewUserOpen(open);
          if (!open) {
            queryClient.invalidateQueries({ queryKey: ['client-users', clientId] });
          }
        }}
      />

      {editingUser && (
        <EditUserDialog
          open={!!editingUser}
          onOpenChange={(open) => {
            if (!open) {
              setEditingUser(null);
              queryClient.invalidateQueries({ queryKey: ['client-users', clientId] });
            }
          }}
          user={editingUser}
        />
      )}
    </>
  );
};
